import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import LanguageSwitcher from "./LanguageSwitcher";
import GuestInstallChip from "@/components/GuestInstallChip";
import NotificationStatusPanel from "@/components/NotificationStatusPanel";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";

export const Header = () => {
  const { lang, t } = useLang();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const links = [
    { to: `/${lang}`, key: "home", end: true },
    { to: `/${lang}/story`, key: "story" },
    { to: `/${lang}/schedule`, key: "schedule" },
    { to: `/${lang}/travel`, key: "travel" },
    { to: `/${lang}/rsvp`, key: "rsvp" },
    { to: `/${lang}/gallery`, key: "gallery" },
    { to: `/${lang}/upload`, key: "upload" },
    { to: `/${lang}/guestbook`, key: "guestbook" },
    { to: `/${lang}/gifts`, key: "gifts" },
    { to: `/${lang}/dj`, key: "dj" },
    { to: `/${lang}/faq`, key: "faq" },
  ];

  return (
    <header
      className="sticky top-0 z-40 border-b border-border/60 backdrop-blur-md bg-background/75"
      data-testid="site-header"
    >
      <div className="mx-auto max-w-[1180px] px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between gap-6">
        <Link
          to={`/${lang}`}
          className="font-serif-display text-lg sm:text-xl tracking-[0.04em] whitespace-nowrap shrink-0"
          data-testid="brand-wordmark"
        >
          Eli <span className="text-accent">&amp;</span> Mag
        </Link>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-6 flex-wrap justify-center flex-1" aria-label="Primary">
          {links.map((l) => (
            <NavLink
              key={l.key}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.key}-link`}
              className={({ isActive }) =>
                cn(
                  "text-[12px] tracking-[0.22em] uppercase transition-colors whitespace-nowrap",
                  isActive
                    ? "text-foreground"
                    : "text-foreground/55 hover:text-foreground"
                )
              }
            >
              {t(`nav.${l.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* Install chip lives in the mobile drawer AND as a mini icon-only in the top bar. */}
          <div className="hidden lg:block">
            <GuestInstallChip variant="desktop" />
          </div>
          <div className="lg:hidden">
            <GuestInstallChip variant="mini" />
          </div>
          <LanguageSwitcher className="hidden sm:flex" />
          <button
            type="button"
            onClick={logout}
            data-testid="site-logout-button"
            className="hidden lg:inline-flex text-[11px] tracking-[0.22em] uppercase text-foreground/45 hover:text-foreground transition-colors"
            title={t("nav.logout")}
          >
            {t("nav.logout")}
          </button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                aria-label={t("nav.menu")}
                data-testid="mobile-menu-trigger"
                className="lg:hidden inline-flex items-center justify-center w-9 h-9 -mr-1 text-foreground"
              >
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-background border-l border-border w-[86vw] sm:w-[420px] p-0 flex flex-col" data-testid="mobile-menu-sheet">
              <SheetHeader className="px-5 pt-3 pb-2 border-b border-border/70 flex flex-row items-center justify-between shrink-0">
                <SheetTitle className="font-serif-display text-base tracking-[0.04em] text-left">Eli &amp; Mag</SheetTitle>
              </SheetHeader>

              {/* Scrollable middle section holds nav + language switcher */}
              <div className="flex-1 min-h-0 overflow-y-auto px-5 py-2">
                <LanguageSwitcher />
                <div className="rule my-2" />
                <nav className="flex flex-col gap-0.5" aria-label="Mobile">
                  {links.map((l) => (
                    <NavLink
                      key={l.key}
                      to={l.to}
                      end={l.end}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-${l.key}-link`}
                      className={({ isActive }) =>
                        cn(
                          "font-serif-display text-[16px] tracking-[0.02em] leading-tight py-1",
                          isActive ? "text-foreground" : "text-foreground/55 hover:text-foreground"
                        )
                      }
                    >
                      {t(`nav.${l.key}`)}
                    </NavLink>
                  ))}
                </nav>
              </div>

              {/* Pinned bottom actions — always visible even on short screens */}
              <div className="shrink-0 border-t border-border/70 px-5 py-2 space-y-1.5 bg-background">
                <NotificationStatusPanel compact />
                <GuestInstallChip variant="mobile" onNavigate={() => setOpen(false)} />
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    logout();
                  }}
                  data-testid="mobile-logout-button"
                  className="w-full text-center text-[10px] tracking-[0.22em] uppercase text-foreground/55 hover:text-foreground py-0.5"
                >
                  {t("nav.logout")}
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
